import { useEffect, useRef, useState } from 'react';
import { InputField, PinCodeField } from '@/components/formParts';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { API_BASE_URL } from '@/config';
import { ClipLoader } from 'react-spinners';

// バリデーション用のスキーマ設定
const schemaEmail = z.object({
  email: z.string().min(1, { message: "メールアドレスは必須です" }).email({ message: "メールアドレス形式が不正です" }),
  email_confirm: z.string().min(1, { message: "メールアドレス確認は必須です" }),
}).superRefine((data, ctx) => {
  if( data.email !== data.email_confirm ) {
    ctx.addIssue({
      code: 'custom',
      message: 'メールアドレスが一致しません',
      path: ['email_confirm'],
    });
  }
});

// const schemaPassword = z.object({
//   password: z.string().min(1, { message: "パスワードは必須です" }),
//   password_confirm: z.string().min(1, { message: "パスワード確認は必須です" }),
// }).superRefine((data, ctx) => {
//   const password = data.password;
//   // 同一文字連続8回以上禁止
//   if (/(.)\1{7,}/.test(password)) {
//     ctx.addIssue({
//       code: 'custom',
//       message: 'パスワードに同一文字を8回以上連続で使用することはできません',
//       path: ['password'],
//     });
//     return;
//   }

//   if (data.password !== data.password_confirm) {
//     ctx.addIssue({
//       code: 'custom',
//       message: 'パスワードが一致しません',
//       path: ['password_confirm'],
//     });
//   }
// });

// const schema = schemaEmail.and(schemaPassword).and(
const schema = schemaEmail.and(
  z.object({
  // code: z.string().min(1, { message: "認証コードは必須です" }),
  code: z.string().regex(/^\d{6}$/, { message: '認証コードは数字6桁で入力してください' }),
})).superRefine((data, ctx) => {
  // if (data.password === data.email) {
  //   ctx.addIssue({
  //     code: 'custom',
  //     message: 'パスワードはメールアドレスと異なるものを設定してください',
  //     path: ['password'],
  //   });
  //   return;
  // }
});

// バリデーション用の型定義
export type RegisterEmailFormData = z.infer<typeof schema>;

// フォームコンポーネント
type RegisterEmailFormProps = {
  onComplete: (data: RegisterEmailFormData) => void;
}

const RegisterEmailForm = ({ onComplete }: RegisterEmailFormProps) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { register, handleSubmit, watch, setValue, resetField, formState: { errors, isValid }, trigger } = useForm<RegisterEmailFormData>({
    mode: 'onTouched',
    resolver: zodResolver(schema),
  });

  const isFormValid = isValid && Object.keys(errors).length === 0;

  const [isLoading, setIsLoading] = useState(false);

  const [isVerified, setIsVerified] = useState(false);
  const [isCodeSent, setIsCodeSent] = useState(false);

  const refVerifyContainer = useRef<HTMLDivElement>(null);
  const refCodeSentContainer = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isVerified && refVerifyContainer.current) {
      refVerifyContainer.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isVerified]);

  useEffect(() => {
    if (isCodeSent && refCodeSentContainer.current) {
      refCodeSentContainer.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isCodeSent]);

  const onVerify = async () => {
    setIsLoading(true);
    setErrorMessage(null); // 前回エラーをリセット
    try {
      const username = "2chiiki-stg-wp";
      const password = "DNt~VqC*N.-5";
      const basicAuth = btoa(`${username}:${password}`);
  
      const response = await fetch(`${API_BASE_URL}/v1/email/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Basic ${basicAuth}`,
        },
        body: JSON.stringify({ email: watch('email') }),
      });
  
      if (!response.ok) {
        // サーバーからのエラーメッセージを読む
        let msg = 'メール送信に失敗しました';
        try {
          const errData = await response.json();
          if (errData?.error) {
            msg = errData.error; // ← サーバーの error フィールドを使う
          }
        } catch (_) {}
        throw new Error(msg);
      }
  
      const data = await response.json();
      console.log('メール送信成功:', data);
      setIsVerified(true);
    } catch (err) {
      console.error('API request failed:', err);
      setErrorMessage(err instanceof Error ? err.message : '不明なエラー');
    } finally {
      setIsLoading(false);
    }
  };

  const onResendCode = () => {
    setIsVerified(false);
    setIsCodeSent(false);
    resetField('code');
    // resetField('password');
    // resetField('password_confirm');
  };

  const onSubmit = async (d: RegisterEmailFormData) => {
    setIsLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    try {
      // const username = "2chiiki-prod";
      // const password = "FJt.QqCaN.+3";
      const username = "2chiiki-stg-wp"
      const password = "DNt~VqC*N.-5"
      const basicAuth = btoa(`${username}:${password}`);
      // const response = await fetch(`${API_BASE_URL}/v1/email/verify?key=${watch('code')}`);
      const response = await fetch(`${API_BASE_URL}/v1/email/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Basic ${basicAuth}`,
        },
        body: JSON.stringify({ key: watch('code') }),
      });
            
      if (!response.ok) {
        // JSONでもテキストでも受けられるようにする
        let rawMessage = '';
        try {
          const data = await response.json();
          rawMessage = data?.error || data?.message || '';
        } catch {
          rawMessage = (await response.text()) || '';
        }
      
        // ユーザー向けに和文へ整形
        const toUserMessage = (msg: string, status: number) => {
          const m = (msg || '').toLowerCase();
          // if (m.includes('expired')) return '認証コードの有効期限が切れています。新しいコードを再送してください。';
          // if (m.includes('invalid')) return '認証コードが正しくありません。もう一度入力してください。';
          if (m.includes('expired') || m.includes('invalid')) {
            return '認証コードが無効か、期限切れです。入力内容を確認して、もう一度お試しください。';
          }
          if (status === 429) return '試行回数の上限に達しました。しばらくしてから再度お試しください。';
          if (status >= 500) return 'サーバーでエラーが発生しました。時間をおいてもう一度お試しください。';
          return '認証に失敗しました。入力内容をご確認のうえ、もう一度お試しください。';
        };
      
        alert(toUserMessage(rawMessage, response.status));
        return;
      }
      
      const data = await response.json();
      console.log('認証成功:', data);
      
      if (data.email) {
        setIsCodeSent(true);
        onComplete(d);
      } else {
        throw new Error('メールアドレスが返されませんでした');
      }
    } catch (err) {
      console.error('認証失敗:', err);
      //alert('認証に失敗しました: ' + (err instanceof Error ? err.message : '不明なエラー'));
      alert((err instanceof Error ? err.message : '不明なエラー'));
    } finally {
      setIsLoading(false);
    }
  };
  // コピー/ペースト抑止用の共通props
  const blockPasteProps = {
    onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => e.preventDefault(),
    onDrop:  (e: React.DragEvent<HTMLInputElement>) => e.preventDefault(),
    onContextMenu: (e: React.MouseEvent<HTMLInputElement>) => e.preventDefault(),
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
      const k = e.key.toLowerCase();
      if ((e.metaKey || e.ctrlKey) && (k === 'v' || k === 'x' || k === 'c')) {
        e.preventDefault();
      }
    },
    // Safari/Chromiumの一部での挿入タイプ対策（React18でも動く簡易版）
    onBeforeInput: (e: any) => {
      if (e?.nativeEvent?.inputType === 'insertFromPaste') e.preventDefault();
    },
  } as const;

  // コピー/カットのみ抑止したい時用（元フィールド）
  const blockCopyCutProps = {
    onCopy: (e: React.ClipboardEvent<HTMLInputElement>) => e.preventDefault(),
    onCut:  (e: React.ClipboardEvent<HTMLInputElement>) => e.preventDefault(),
    onContextMenu: (e: React.MouseEvent<HTMLInputElement>) => e.preventDefault(),
  } as const;
  // const passwordValue = watch('password');
  // const passwordEnoughLength = passwordValue?.length >= 8;
  // const passwordHasSymbol = passwordValue?.match(/[\W_]/);
  // const passwordHasLowercase = passwordValue?.match(/[a-z]/);
  // const passwordHasUppercase = passwordValue?.match(/[A-Z]/);
  // const passwordHasNumber = passwordValue?.match(/\d/);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-col gap-8">
        <InputField label="メールアドレス" type="email" register={register('email')} errors={errors} hasRequiredLabel={true} readOnly={isVerified || isCodeSent} className={isVerified || isCodeSent ? 'protected' : ''} onContextMenu={(e) => e.preventDefault()} />
        {(!isVerified) && (
          <>
            <InputField
              label="メールアドレス確認"
              type="email"
              register={register('email_confirm')}
              errors={errors}
              hasRequiredLabel={true}
              autoComplete="off"
              inputMode="email"
              {...blockPasteProps}
            />
            <div className="flex justify-center mt-8">
              <button type="button" className="m-btn fill" disabled={!watch('email') || !!errors.email || !!errors.email_confirm} onClick={onVerify}>認証コード送信</button>
            </div>
            {isLoading && <div className="flex justify-center"><ClipLoader color="#000" loading={isLoading} /></div>}
          </>
        )}
      </div>
      {errorMessage && (
        <div className="text-red-600 text-center mt-4">
          {errorMessage}
        </div>
      )}
      {isVerified && !isCodeSent && (
        <div className="flex flex-col gap-8 mt-8">
          <div className="text-center bg-yellow-100 py-8 px-2 rounded-lg border border-yellow-300" ref={refVerifyContainer}>
            <p><strong>{watch('email')}</strong>宛に認証コードを送信しました。<br />メールに記載の認証コードをご入力ください。</p>
          </div>
          <div>
            <div className="w-[25rem]">
              {/* <PinCodeField label="認証コード" register={register('code')} errors={errors} hasRequiredLabel={true} length={6} /> */}
              <InputField
  label="認証コード"
  type="tel"
  inputMode="numeric"
  autoComplete="one-time-code"
  {...register('code')}
  value={watch('code') || ''}
  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
    const v = (e.target.value || '').replace(/\D/g, '').slice(0, 6);
    setValue('code', v, { shouldValidate: true, shouldDirty: true });
    // trigger('code'); // ここで即時バリデしたいなら有効化
  }}
/>
              {/* <PinCodeField label="認証コード" register={register('code')} errors={errors} length={6} onChange={(value) => {setValue('code', value); trigger('code')}} value={watch('code')} /> */}
            </div>
            {/* <InputField label="認証コード" type="text" register={register('code')} errors={errors} hasRequiredLabel={true} /> */}
            {/* <p className="text-gray-500"><small>※一旦認証URLのkeyで代用（?key=xxxxxxのxxxxxxを入力）</small></p> */}
          </div>
          {/* <div>
            <div className="grid xmd:grid-cols-2 gap-x-4 gap-y-4">
              <InputField label="パスワード" type="password" register={register('password')} errors={errors} hasRequiredLabel={true} onContextMenu={(e) => e.preventDefault()} />
              <InputField label="パスワード確認" type="password" register={register('password_confirm')} errors={errors} hasRequiredLabel={true} onContextMenu={(e) => e.preventDefault()} />
            </div>
            <div className="text-gray-500 mt-2">
              <p><small>※パスワードは8文字以上で英大文字・小文字・数字・記号を含めてください</small></p>
              <ul className="flex flex-col gap-x-4 gap-y-2 mt-2">
                <li className="flex items-center"><span>{passwordEnoughLength ? <MdCheck color="green" /> : <MdError color="gray" />}</span><small>8文字以上</small></li>
                <li className="flex items-center"><span>{passwordHasLowercase ? <MdCheck color="green" /> : <MdError color="gray" />}</span><small>半角英小文字</small></li>
                <li className="flex items-center"><span>{passwordHasUppercase ? <MdCheck color="green" /> : <MdError color="gray" />}</span><small>半角英大文字</small></li>
                <li className="flex items-center"><span>{passwordHasNumber ? <MdCheck color="green" /> : <MdError color="gray" />}</span><small>半角数字</small></li>
                <li className="flex items-center"><span>{passwordHasSymbol ? <MdCheck color="green" /> : <MdError color="gray" />}</span><small>記号（!@#$%^&*_-）</small></li>
              </ul>
            </div>
          </div> */}
          <div className="flex justify-center mt-8">
            <button type="submit" className={`m-btn fill ${!isFormValid ? 'disabled' : ''}`}>認証コード登録</button>
          </div>
          {isLoading && <div className="flex justify-center"><ClipLoader color="#000" loading={isLoading} /></div>}
          <div className="flex justify-center">
            <button type="button" className="m-btn outlined" onClick={onResendCode}>認証をやり直す</button>
          </div>
        </div>
      )}
      {
        isCodeSent && (
          <div className="text-center bg-yellow-100 py-8 px-2 rounded-lg border border-yellow-300 mt-8" ref={refCodeSentContainer}>
            <p>メール認証が完了しました。<br />STEP2〜STEP4の各項目をご入力ください。</p>
            <div className="flex justify-center mt-8">
              <button type="button" className="m-btn btn-small outlined" onClick={onResendCode}>認証をやり直す場合はこちら</button>
            </div>
          </div>
          
        )
      }
    </form>
  );
}

export default RegisterEmailForm;
